/**
 * The Pomiary tab's wiring: two reads, one projection, one write, and the two pieces of state the
 * tab owns — where A and B stand, and what is typed in the drawer.
 *
 * The form's VALIDATION is not here — it is `model/measurements`' `buildMeasurementRequest`. Where
 * the markers may go is not here either — it is `model/twoDates`' `moveSelection`, which is the one
 * place the "snap to a real measurement, never cross the other marker" rules live. What this owns
 * is which fields are currently painted invalid, and that they stop being painted the moment the
 * trainer edits one.
 *
 * ── The selection is DERIVED until the trainer moves it ──────────────────────────────────────
 * `null` means "nobody has chosen yet", and the whole loaded history is what the tab opens on. A
 * default written into state by an effect would mean one frame with no markers and then a frame
 * with them, which is the jump this app refuses; deriving it means the first painted frame is the
 * final one. The client id rides along with it, because a change of client leaves instants from
 * somebody else's history behind.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePostCustomerIdBody } from '~/shared/api/generated/endpoints/customer/customer';

import {
  BODY_HISTORY_LIMIT,
  clientBodyHistoryQueryKey,
  clientBodyHistoryQueryOptions,
  clientBodyQueryKey,
  clientBodyQueryOptions,
  clientGoalQueryKey,
} from '../api/clientDetailQueries';
import { toMeasurementSnapshots } from '../api/clientDetailSource';
import {
  buildMeasurementRequest,
  EMPTY_MEASUREMENT_FORM,
  type MeasurementFormField,
  type MeasurementFormValues,
  type MeasurementParameter,
} from '../model/measurements';
import {
  buildTwoDates,
  defaultSelection,
  moveSelection,
  type SelectionMove,
  type TwoDatesPreset,
  type TwoDatesSelection,
  type TwoDatesView,
} from '../model/twoDates';

export interface MeasurementsOptions {
  readonly parameter: MeasurementParameter;
  readonly trend: boolean;
  readonly projection: boolean;
}

export interface MeasurementsState {
  readonly view: TwoDatesView;
  /**
   * The tab cannot answer its own question.
   *
   * It is the HISTORY read alone, and that is a change of shape rather than of wording. It used to
   * be `history.isError && body.isError`, which was right when the tab drew a "latest measurement"
   * card `/body` could fill on its own. Direction D makes a COMPARATIVE claim: the markers, the
   * sentence, the eleven comparison rows and the plot are every one of them built from
   * `/body/history`, and `/body` contributes only the newest snapshot. A failed history with a
   * successful `/body` therefore leaves nothing the tab could honestly draw — and before this it
   * drew eleven „bez pary" rows and "Ten klient nie ma jeszcze pomiarów", both of them assertions
   * about a client whose measurements were never read.
   */
  readonly failed: boolean;
  /** Drop both reads and ask again — what the failed state's own retry is wired to. */
  readonly retry: () => void;
  /**
   * Whether the reads are still in flight.
   *
   * Distinct from `view.empty`, and that distinction is the whole point of it: a client with no
   * measurements and a client whose measurements have not arrived yet look identical in the view,
   * and drawing the "no measurements" sentence for the second one makes the card two hundred pixels
   * shorter for a moment and then jump.
   */
  readonly pending: boolean;
  /** Every way A and B move — a preset, an arrow, a drag, a click on the plot. */
  readonly move: (move: SelectionMove) => void;
  readonly values: MeasurementFormValues;
  readonly invalid: readonly MeasurementFormField[];
  readonly setField: (field: MeasurementFormField, value: string) => void;
  /**
   * Send it, or say which fields refused — RETURNED rather than only painted, so the drawer can
   * move focus to the first of them. `onSaved` runs after the write lands, which is what closes the
   * drawer.
   *
   * ── Closing without saving KEEPS what was typed ─────────────────────────────────────────────
   * There is deliberately no `reset`. Escape is one keystroke away from every field in this drawer,
   * and a reset on close would make a slip of it cost nine typed numbers; reopening and finding the
   * values still there costs nothing, because the only way anything reaches the server is this
   * call. A successful save clears the form itself, which is the one moment the values are spent.
   */
  readonly submit: (onSaved?: () => void) => readonly MeasurementFormField[];
  readonly saving: boolean;
  readonly saveFailed: boolean;
}

interface HeldSelection {
  readonly clientId: number;
  readonly selection: TwoDatesSelection;
}

interface HeldPreset {
  readonly clientId: number;
  readonly preset: TwoDatesPreset;
}

export function useMeasurements(
  clientId: number,
  options: MeasurementsOptions,
): MeasurementsState {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [values, setValues] = useState<MeasurementFormValues>(EMPTY_MEASUREMENT_FORM);
  const [invalid, setInvalid] = useState<readonly MeasurementFormField[]>([]);
  const [held, setHeld] = useState<HeldSelection | null>(null);
  /**
   * Which preset the trainer last pressed, and it rides with the CLIENT for the same reason the
   * selection does. Cleared by any move that is not a preset, because a marker dragged by hand is
   * the trainer saying the chip no longer describes what they are looking at.
   */
  const [chosenPreset, setChosenPreset] = useState<HeldPreset | null>(null);

  const body = useQuery(clientBodyQueryOptions(clientId));
  const history = useQuery(clientBodyHistoryQueryOptions(clientId));
  const post = usePostCustomerIdBody();

  const snapshots =
    history.isSuccess || body.isSuccess ? toMeasurementSnapshots(body.data, history.data) : null;
  const selection =
    held !== null && held.clientId === clientId ? held.selection : defaultSelection(snapshots);

  return {
    view: buildTwoDates(
      {
        snapshots,
        parameter: options.parameter,
        selection,
        chosenPreset:
          chosenPreset !== null && chosenPreset.clientId === clientId ? chosenPreset.preset : null,
        // The backend drops the OLDEST rows past the cap, so a read that came back exactly full
        // cannot claim to hold the client's first measurement.
        atCap: (history.data?.items?.length ?? 0) >= BODY_HISTORY_LIMIT,
        trend: options.trend,
        projection: options.projection,
      },
      { t, locale: i18n.language },
    ),
    failed: history.isError,
    retry: () => {
      void history.refetch();
      void body.refetch();
    },
    /**
     * `pending` is an `||` over BOTH reads while `failed` names only the history, and the asymmetry
     * is deliberate in both directions.
     *
     * A failed `/body` alone still leaves the history to draw everything from, so it is not a
     * failure of the tab. But the tab is still LOADING while either read is open: the projection
     * above is built the moment one of them succeeds, so an `&&` here flipped `pending` to false
     * with a request still in flight — the chart's reserved box came down, the "no measurements"
     * sentence went up in its place, and the cards below it moved. Measured on the running tab on
     * 1 September 2026.
     */
    pending: history.isPending || body.isPending,
    move: (move) => {
      const next = moveSelection(snapshots, selection, move);
      if (next !== null) setHeld({ clientId, selection: next });
      setChosenPreset(move.kind === 'preset' ? { clientId, preset: move.preset } : null);
    },
    values,
    invalid,
    setField: (field, value) => {
      setValues((current) => ({ ...current, [field]: value }));
      // Cleared on edit and re-checked on the next submit, so a half-typed `6` does not read as
      // out of range while the trainer is still typing `64,2`.
      setInvalid((current) => current.filter((entry) => entry !== field));
    },
    submit: (onSaved) => {
      // A second press while the first write is in flight is swallowed rather than refused by a
      // `disabled` button, which would take the primary out of the tab order mid-request.
      if (post.isPending) return [];
      const submission = buildMeasurementRequest(values);
      setInvalid(submission.invalid);
      if (submission.request === null) return submission.invalid;
      post.mutate(
        { id: clientId, data: submission.request },
        {
          onSuccess: () => {
            setValues(EMPTY_MEASUREMENT_FORM);
            // A new measurement reshapes the trend and re-derives the server's own relative
            // labels, so both reads are dropped rather than patched.
            void queryClient.invalidateQueries({ queryKey: clientBodyQueryKey(clientId) });
            void queryClient.invalidateQueries({ queryKey: clientBodyHistoryQueryKey(clientId) });
            // The goal's `currentValue` is derived from measurements on the server, so the summary
            // column's goal card moves with this write too — found by hand: the card kept saying
            // "Brak pomiaru" beside a snapshot that already showed the weight (12 Aug 2026).
            void queryClient.invalidateQueries({ queryKey: clientGoalQueryKey(clientId) });
            onSaved?.();
          },
        },
      );
      return [];
    },
    saving: post.isPending,
    saveFailed: post.isError,
  };
}
